import {setUserId, logEvent, getAnalytics} from '@react-native-firebase/analytics';
import {getCrashlytics, log, recordError, crash, sendUnsentReports, setUserId as crashlyticsSetUserId, setAttributes} from '@react-native-firebase/crashlytics';
import { Platform } from 'react-native';
import config from '../config';
// Note: WizzeeLog is NOT imported here to avoid a circular dependency
// (wizzeeLog.js imports from analytics.js). logDev() is __DEV__-only
// debug tracing — console.log is intentional and sufficient here.

const isAvailable = Platform.OS !== 'web' || config.GOOGLE_ANALYTICS_KEY;
const fbAnalytics = getAnalytics();
const fbCrashlytics = getCrashlytics();

export const trackAnalyticsEvent = (action, optionalValues) => {
  if (isAvailable) {
    const params = optionalValues && typeof optionalValues === 'object' ? optionalValues : undefined;
    logEvent(fbAnalytics, action, params).then( () => { logDev('trackAnalyticsEvent', action); } );
  }
};

export const trackAnalyticsScreenView = async sceneKey => {
  if (isAvailable && sceneKey) {
    const truncatedSceneKey = `scene_view_${sceneKey}`.substring(0, 40); // substring to avoid going over firebase 'Names should contain 1 to 40 alphanumeric characters or underscores' error
    trackAnalyticsEvent(`${truncatedSceneKey}`); 
  }
};

export const reportError = error => {
  const normalizedError = error instanceof Error ? error : new Error(String(error?.message || error || 'Unknown'));
  const code = error?.code || -1;

  trackAnalyticsEvent('custom_error', {
    message: normalizedError.message,
    code,
  });

  // Record as non-fatal error in Crashlytics so the full stack trace is
  // visible in the Firebase console (under Crashlytics > Non-fatals).
  // recordError returns void in @react-native-firebase v23 — do not chain .catch().
  if (isAvailable) {
    recordError(fbCrashlytics, normalizedError);
  }
};

// Adds a breadcrumb string to the current Crashlytics session log.
// Visible in crash/non-fatal reports under "Logs" in the Firebase console.
// log() returns void in @react-native-firebase v23 — do not chain .catch().
export const reportCrashLog = message => {
  if (isAvailable) {
    log(fbCrashlytics, String(message));
  }
};

export const logUser = userId => {
  if (isAvailable) {
    setUserId(fbAnalytics, userId).then( () => {
      logDev('firebaseAnalytics::setUserId', userId);
    });
    crashlyticsSetUserId(fbCrashlytics, userId).then( () => {
      logDev('crashlytics::crashlyticsSetUserId', userId);
    });
  }
};

// Attach human-readable identifiers to the Crashlytics session so every
// crash/non-fatal report can be matched to a specific user without having
// to look up the opaque externalId. Visible under "Keys" in each report.
export const setUserContext = ({ email, msisdn }: { email?: string, msisdn?: string }) => {
  if (!isAvailable) return;
  const attrs = {};
  if (email)  attrs.email  = email;
  if (msisdn) attrs.msisdn = msisdn;
  if (Object.keys(attrs).length > 0) {
    setAttributes(fbCrashlytics, attrs).catch(() => {});
  }
};

// Forces all queued non-fatal Crashlytics reports to be sent immediately.
// Non-fatals are normally batched and sent on the next app launch — use this
// during development/QA to verify reports appear in the Firebase Console
// without having to restart the app.
export const flushCrashlyticsReports = () => {
  if (isAvailable) {
    sendUnsentReports(fbCrashlytics);
  }
};

export const forceCrash = () => {
  if (isAvailable) {
    log(fbCrashlytics, 'Crash via CheatCode');
    crash(fbCrashlytics);
  }
};

const logDev = (name: string, value?: any) => {
  if (__DEV__) {
    console.log(`>>> logDev()::${name} ${value}`);
  }
};
